import React, { ChangeEvent } from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { FormControl, FormLabel, FormErrorMessage, Text, Checkbox, CheckboxGroup } from '@chakra-ui/react';
import { FormFieldPropTypes } from './FormFieldPropTypes';
import { IS_REQUIRED } from '../../utils/validators';
import useConstructor from '../../hooks/useConstructor';


export const FormField = (
    {
        name,
        label,
        required,
        validate,
        children,
        onChange,
        onBlur,
        onFocus,
        wrapperProps = {},
        inputStyleProps = {},
        showInitialError = false,
        ...remainingProps
    }: FormFieldPropTypes.IFormFieldProps
) => {

    const validateField = (value: any) => {
        const { required: requiredValidator } = IS_REQUIRED
        const allValidators = { ...validate, ...required && { required: { ...requiredValidator, label } } }
        let error = ''
        Object.values(allValidators).forEach(({ validator, ...rest }) => {
            if (!error) error = validator(value, { ...rest })
        })
        return error
    }

    return (
        <Field
            name={name}
            validate={validateField}
            render={({ input, meta: { touched = false, error = '', initial } }: FieldRenderProps<any, HTMLElement>) => {
                const errorMessage = (showInitialError || touched) && error
                const errorColorProps = errorMessage ? { color: 'error.main' } : {}
                let componentProps = {}

                useConstructor(() => {
                    if (children.type == Checkbox) {
                        componentProps = { defaultChecked: initial }
                    } else if (children.type == CheckboxGroup) {
                        componentProps = { defaultValues: initial }
                    } else {
                        componentProps = { defaultValue: initial }
                    }
                })

                const onChangeFieldValue = (event: ChangeEvent<HTMLInputElement>): void => {
                    input.onChange(event);
                    if (!touched && input.onBlur) input.onBlur();
                    if (onChange) onChange(event);
                };

                const onBlurFieldValue = (event: ChangeEvent<HTMLInputElement>): void => {
                    if (onBlur) onBlur(event);
                };

                const onFocusFieldValue = (event: ChangeEvent<HTMLInputElement>): void => {
                    if (onFocus) onFocus(event);
                };

                return (
                    <FormControl isInvalid={!!errorMessage} {...wrapperProps}>
                        <FormLabel htmlFor={name} {...errorColorProps} display={label ? 'inherit' : 'none'}>
                            <Text textStyle="body1">{label}{label && required && <span>*</span>}</Text>
                        </FormLabel>
                        {React.cloneElement(children, {
                            onChange: onChangeFieldValue,
                            onBlur: onBlurFieldValue,
                            onFocus: onFocusFieldValue,
                            ...componentProps,
                            ...remainingProps,
                            ...inputStyleProps,
                            value: input.value,
                            ...errorMessage && { _invalid: { borderColor: 'error.main', boxShadow: 'none' } }
                        })}
                        <FormErrorMessage {...errorColorProps}>{errorMessage}</FormErrorMessage>
                    </FormControl>
                );
            }}
        />
    )
}
